import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { API_ENDPOINTS } from 'app/core/constants/api-endpoints.constant';
import { ApiService } from 'app/shared/constant/api-service/api.service';
import { Observable, Subject } from 'rxjs';
import moment from 'moment';

@Injectable({
    providedIn: 'root',
})
export class ApplicationService {
    sendCalendarEventId = new Subject<number>();
    constructor(
        private _apiService: ApiService,
        private router: Router,
    ) {}

    set accessToken(token: string) {
        localStorage.setItem('accessToken', token);
    }

    get accessToken(): string {
        return localStorage.getItem('accessToken') ?? '';
    }

    set idToken(token: string) {
        localStorage.setItem('idToken', token);
    }

    get idToken(): string {
        return localStorage.getItem('idToken') ?? '';
    }

    checkCaseIdForRedirection(caseId) {
        if (!caseId) return this.router.navigate(['/business-financing']);
    }

    getApplicationProductList(productType?: string): Observable<any> {
        if (productType) {
            return this._apiService.get(
                `${API_ENDPOINTS.GET_APP_PRODUCTS}?productType=` + productType,
                {
                    headers: { skip: 'true' },
                },
            );
        } else {
            return this._apiService.get(`${API_ENDPOINTS.GET_APP_PRODUCTS}`, {
                headers: { skip: 'true' },
            });
        }
    }

    getNoMatchRecommendation(): Observable<any> {
        return this._apiService.get(
            `${API_ENDPOINTS.GET_NO_MATCH_RECOMMENDATION}`,
        );
    }

    setProductData(productData: any): Observable<any> {
        return this._apiService.post(
            `${API_ENDPOINTS.SET_PRODUCT}`,
            productData,
        );
    }

    createCaseProduct(data): Observable<any> {
        return this._apiService.post(`${API_ENDPOINTS.CREATE_CASE}`, data);
    }

    getStates(): Observable<any> {
        return this._apiService.get(`${API_ENDPOINTS.GET_STATES}`);
    }

    getCities(state: string): Observable<any> {
        return this._apiService.get(
            `${API_ENDPOINTS.GET_CITIES}?state=` + state,
        );
    }

    submitFinancingInfo(financingInfoData: any): Observable<any> {
        return this._apiService.post(
            `${API_ENDPOINTS.FINANCING_INFO}`,
            financingInfoData,
        );
    }

    checkBRN(data: any): Observable<any> {
        return this._apiService.post(`${API_ENDPOINTS.CHECK_BRN}`, data, {
            observe: 'response',
        });
    }

    refreshDetails(refreshDetails: any): Observable<any> {
        return this._apiService.post(
            `${API_ENDPOINTS.REFRESH_DETAILS}`,
            refreshDetails,
            {
                observe: 'response',
            },
        );
    }

    checkKYC(data: any): Observable<any> {
        return this._apiService.post(`${API_ENDPOINTS.CHECK_KYC}`, data);
    }

    businessInfoData(data: any): Observable<any> {
        return this._apiService.post(`${API_ENDPOINTS.BUSINESS_INFO}`, data);
    }

    checkEligibility(data: any): Observable<any> {
        return this._apiService.post(
            `${API_ENDPOINTS.CHECK_ELIGIBILITY}`,
            data,
            {
                observe: 'response',
            },
        );
    }

    getProductRecommendation(caseId: any): Observable<any> {
        return this._apiService.post(
            `${API_ENDPOINTS.GET_RECOMMENDATION}`,
            caseId,
            {
                observe: 'response',
            },
        );
    }

    getCaseDetails(caseId: any): Observable<any> {
        return this._apiService.get(
            `${API_ENDPOINTS.GET_CASE}?caseId=` + caseId,
        );
    }

    submission(data: any): Observable<any> {
        return this._apiService.post(`${API_ENDPOINTS.SUBMISSION}`, data);
    }

    getCalendarDetails(month, year): Observable<any> {
        return this._apiService.get(
            `${API_ENDPOINTS.CALENDAR}?month=` + month + `&year=` + year,
            {
                headers: { skip: 'true' },
            },
        );
    }

    getWorkshopQuestions(id: any): Observable<any> {
        return this._apiService.post(
            `${API_ENDPOINTS.GET_WORKSHOP_QUESTION}`,
            id,
        );
    }

    submitWorkshopQuestions(data: any): Observable<any> {
        return this._apiService.post(
            `${API_ENDPOINTS.SUBMIT_WORKSHOP_QUESTION}`,
            data,
        );
    }

    getGalleryImages(): Observable<any> {
        return this._apiService.get(`${API_ENDPOINTS.GET_GALLERY}`, {
            headers: { skip: 'true' },
        });
    }

    uploadDocument(
        docCheckBox: any,
        caseId: string,
        draftId: any,
        file?: File,
        alias?: string,
        password?: string,
    ): Observable<any> {
        const formData: FormData = new FormData();
        formData.append('docCheckBox', docCheckBox);
        formData.append('caseId', caseId);
        formData.append('saveDraft', draftId);
        if (file) formData.append('file', file);
        if (alias) formData.append('alias', alias);
        if (password) formData.append('password', password);

        return this._apiService.post(API_ENDPOINTS.UPLOAD_DOCUMENT, formData);
    }

    deleteDocument(docsId: string, caseId: string): Observable<any> {
        const formData: FormData = new FormData();
        formData.append('docsId', docsId);
        formData.append('caseId', caseId);

        return this._apiService.post(API_ENDPOINTS.DELETE_DOCUMENT, formData);
    }

    viewDocument(data: any): Observable<any> {
        return this._apiService.post(API_ENDPOINTS.VIEW_DOCUMENT, data);
    }

    getEventDetails(id: any): Observable<any> {
        return this._apiService.get(`${API_ENDPOINTS.GET_EVENT_DETAILS}` + id, {
            headers: { skip: 'true' },
        });
    }

    getCampaign(category: any, currentDate?: boolean): Observable<any> {
        if (category === 'WHATSNEW-NEWS') {
            return this._apiService.get(
                `${API_ENDPOINTS.GET_ALL_CAMPAIGN}` +
                    `?limit=1000&offset=0&category=${category}&status=ACTIVE&sort=sequenceNo,asc&sort=modifiedDate,desc&bannerStartDate$between=1900-1-1 00:00:00,${moment.utc().format('YYYY-MM-DD')} 23:59:59`,
                {
                    headers: { skip: 'true' },
                },
            );
        } else if (category === 'WHATSNEW-CAMPAIGN') {
            return this._apiService.get(
                `${API_ENDPOINTS.GET_ALL_CAMPAIGN}` +
                    `?limit=1000&offset=0&category=${category}&status=ACTIVE&sort=sequenceNo,asc&sort=modifiedDate,desc&campaignStartDate$between=1900-1-1 00:00:00,${moment.utc().format('YYYY-MM-DD')} 23:59:59&campaignEndDate$between=${moment.utc().format('YYYY-MM-DD')} 00:00:00,9999-1-1 23:59:59`,
                {
                    headers: { skip: 'true' },
                },
            );
        } else {
            return this._apiService.get(
                `${API_ENDPOINTS.GET_ALL_CAMPAIGN}` +
                    `?limit=1000&offset=0&category=${category}&status=ACTIVE&sort=sequenceNo,asc&sort=modifiedDate,desc${currentDate ? `&bannerStartDate$between=1900-1-1 00:00:00,${moment.utc().format('YYYY-MM-DD')} 23:00:00&bannerEndDate$between=${moment.utc().format('YYYY-MM-DD')} 00:00:00,9999-1-1 23:00:00` : ''}`,
                {
                    headers: { skip: 'true' },
                },
            );
        }
    }

    getCampaignDetailsByCampaignName(campaignName: any): Observable<any> {
        return this._apiService.get(
            `${API_ENDPOINTS.GET_ALL_CAMPAIGN}` +
                '?status=ACTIVE&campaigndetails.campaignName=' +
                campaignName,
            {
                headers: { skip: 'true' },
            },
        );
    }

    getCampaignDetailsById(id: any): Observable<any> {
        return this._apiService.get(
            `${API_ENDPOINTS.GET_CAMPAIGN_DETAILS_BY_ID}` + id,
            {
                headers: { skip: 'true' },
            },
        );
    }

    getPartnership(): Observable<any> {
        return this._apiService.get(
            `${API_ENDPOINTS.GET_ALL_PARTNERSHIP}` +
                '?limit=1000&status=ACTIVE',
            {
                headers: { skip: 'true' },
            },
        );
    }

    getPartnershipDetailsById(id: any): Observable<any> {
        return this._apiService.get(
            `${API_ENDPOINTS.GET_PARTNERSHIP_DETAILS_BY_ID}` + id,
            {
                headers: { skip: 'true' },
            },
        );
    }

    getDigiServiceData(): Observable<any> {
        return this._apiService.get(`${API_ENDPOINTS.GET_DIGI_SERVICE_DATA}`);
    }
}
